<template>
  <div
    style="
      height: 48px;
      flex: none;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 0 16px;
      white-space: nowrap;
      background: var(--surface-2);
      border-bottom: 1px solid var(--line);
    ">
    <span style="font-size: 16px; font-weight: 800; letter-spacing: -0.01em; color: #fff"
      >In<span style="color: var(--accent)">•</span>Your<span style="color: var(--accent)">•Place</span></span
    >
    <span class="mono" style="font-size: 10px; letter-spacing: 0.18em; color: var(--text-ph)">MAM</span>
    <span class="mv-sep"></span>
    <!-- ponytail: static format label — no fps field on Asset yet -->
    <span class="mono" style="font-size: 10.5px; color: var(--text-faint)">25&nbsp;fps · 1080i50</span>

    <div style="margin-left: auto; display: flex; align-items: center; gap: 14px">
      <template v-if="storage">
        <div style="display: flex; align-items: center; gap: 8px" :title="`Storage ${storageFigures.used} of ${storageFigures.total} used`">
          <span class="mono" style="font-size: 10px; letter-spacing: 0.1em; color: var(--text-faint)">STORAGE</span>
          <div style="width: 80px; height: 5px; border-radius: 99px; background: var(--surface-4); overflow: hidden">
            <div :style="{ height: '100%', width: storageUsedPercent + '%', background: storageBar }"></div>
          </div>
          <span class="mono" style="font-size: 10.5px; color: var(--text-dim)">{{ storageFigures.used }} / {{ storageFigures.total }}</span>
        </div>
        <span class="mv-sep"></span>
      </template>
      <!-- ponytail: static language switcher — no i18n yet -->
      <span class="mv-ghost mv-ghost--bare">EN ▾</span>
      <div style="position: relative">
        <div class="mv-user" :title="'Role: ' + roleLabel" @click="userMenuOpen = !userMenuOpen">
          <div class="mv-avatar">{{ userInitials }}</div>
          <span ellipsis style="max-width: 160px; font-size: 12.5px; font-weight: 600; color: var(--text)">{{ username }}</span>
          <span style="font-size: 9px; color: var(--text-faint)">▾</span>
        </div>
        <template v-if="userMenuOpen">
          <div class="mv-user-scrim" @click="userMenuOpen = false"></div>
          <div class="mv-user-menu">
            <div class="mv-user-menu__who">
              <div ellipsis style="font-size: 12.5px; font-weight: 600; color: var(--text)">{{ username }}</div>
              <div class="mono" style="font-size: 10px; color: var(--text-faint)">{{ roleLabel }}</div>
            </div>
            <div v-if="$store.state.authUser.admin" class="mv-user-menu__item" @click="openAdmin()">Administration</div>
            <div class="mv-user-menu__item" @click="signOut()">Sign out</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { logout } from "@/scripts/keycloak.js";

// Both figures share a unit so the ratio stays readable; the total picks it.
const SIZE_UNITS = [
  ["TB", 1024 ** 4],
  ["GB", 1024 ** 3],
  ["MB", 1024 ** 2],
];

const ROLE_LABELS = { MANAGE: "Manager", EDIT: "Editor", VIEW: "Viewer" };

export default {
  data: () => {
    return { userMenuOpen: false, storage: null };
  },

  computed: {
    storageUsedPercent() {
      return Math.min(100, Math.round((this.storage.usedBytes / this.storage.totalBytes) * 100));
    },
    storageBar() {
      const full = this.storageUsedPercent;
      if (full >= 90) return "var(--st-error)";
      if (full >= 75) return "var(--st-processing)";
      return "var(--st-ready)";
    },
    /** e.g. { used: "30.8", total: "41.9 TB" } */
    storageFigures() {
      const [name, size] = SIZE_UNITS.find(([, size]) => this.storage.totalBytes >= size) || SIZE_UNITS[SIZE_UNITS.length - 1];
      return { used: (this.storage.usedBytes / size).toFixed(1), total: `${(this.storage.totalBytes / size).toFixed(1)}\u00a0${name}` };
    },
    username() {
      return this.$store.state.authUser.username || "Account";
    },
    roleLabel() {
      const user = this.$store.state.authUser;
      if (user.admin) return "Admin";
      return ROLE_LABELS[user.rootPermission] || "User";
    },
    userInitials() {
      const name = this.$store.state.authUser.username || "";
      const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
      if (!parts.length) return "—";
      return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
    },
  },
  mounted() {
    // ponytail: read once. A meter that moves with every ingest would need the websocket.
    this.$store.state.loadStorage((error, data) => {
      if (!error && data && data.totalBytes > 0) this.storage = data;
    });
  },
  methods: {
    openAdmin() {
      this.userMenuOpen = false;
      this.$store.state.adminOpen = true;
    },
    signOut() {
      this.userMenuOpen = false;
      logout();
    },
  },
};
</script>

<style scoped>
.mv-user {
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px 0 4px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.12s;
}
.mv-user:hover {
  background: var(--surface-3);
}
.mv-avatar {
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: 50%;
  background: var(--accent-bg);
  border: 1px solid var(--accent-bd);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--accent-soft);
}
/* catches the next click anywhere, so the menu closes without a document listener */
.mv-user-scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
}
.mv-user-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 41;
  min-width: 180px;
  max-width: 260px;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-2);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
}
.mv-user-menu__who {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 7px 10px 9px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--line-inner);
  cursor: default;
}
.mv-user-menu__item {
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 12.5px;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
}
.mv-user-menu__item:hover {
  background: var(--surface-3);
}
</style>
